import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { paymentsAPI } from '../lib/api';
import { getProvider, setProvider as persistProvider } from '../lib/region';

// Plan display data per currency. Annual = 2 months free (10x monthly, billed once).
const PLAN_DATA = {
  INR: {
    starter_monthly:      { name: 'Starter', price: 24500, period: 'month', billing: 'monthly' },
    starter_annual:       { name: 'Starter', price: 19600, period: 'month', billing: 'annual', billedTotal: 235200, savings: 58800 },
    professional_monthly: { name: 'Growth',  price: 49000, period: 'month', billing: 'monthly' },
    professional_annual:  { name: 'Growth',  price: 40833, period: 'month', billing: 'annual', billedTotal: 490000, savings: 98000 },
  },
  USD: {
    starter_monthly:      { name: 'Starter', price: 250, period: 'month', billing: 'monthly' },
    starter_annual:       { name: 'Starter', price: 208, period: 'month', billing: 'annual', billedTotal: 2500, savings: 500 },
    professional_monthly: { name: 'Growth',  price: 500, period: 'month', billing: 'monthly' },
    professional_annual:  { name: 'Growth',  price: 417, period: 'month', billing: 'annual', billedTotal: 5000, savings: 1000 },
  },
};

const money = (n, currency) => currency === 'USD'
  ? `$${Number(n || 0).toLocaleString('en-US')}`
  : `\u20B9${Number(n || 0).toLocaleString('en-IN')}`;

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function PaymentPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const planKey = searchParams.get('plan') || 'professional_monthly';

  const [provider, setProvider] = useState(getProvider()); // 'stripe' | 'razorpay'
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState('payment'); // 'payment' | 'success'

  const currency = provider === 'stripe' ? 'USD' : 'INR';
  const plan = PLAN_DATA[currency][planKey] || PLAN_DATA[currency].professional_monthly;

  useEffect(() => {
    if (!user) navigate('/login?redirect=payment&plan=' + planKey);
  }, [user, navigate, planKey]);

  const chooseProvider = (p) => { setProvider(p); persistProvider(p); };

  const totalLabel = plan.billing === 'annual'
    ? `${money(plan.billedTotal, currency)}/yr`
    : `${money(plan.price, currency)}/mo`;

  const handlePayStripe = async () => {
    setProcessing(true);
    try {
      const { data } = await paymentsAPI.stripeCreatePlanSession(planKey, window.location.origin);
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error('Could not start the checkout. Please try again.');
      setProcessing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start the checkout. Please try again.');
      setProcessing(false);
    }
  };

  const handlePayRazorpay = async () => {
    setProcessing(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error('Could not load the payment gateway. Please retry.');
        setProcessing(false);
        return;
      }
      const { data } = await paymentsAPI.createOrder(planKey);
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: 'CHIRE',
        description: `${data.plan_name} plan`,
        order_id: data.order_id,
        prefill: { name: data.prefill?.name || '', email: data.prefill?.email || '' },
        theme: { color: '#0076A3' },
        handler: async (resp) => {
          try {
            await paymentsAPI.verify({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setStep('success');
            toast.success('Payment successful! Your subscription is now active.');
          } catch (e) {
            toast.error('Payment verification failed. Please contact support.');
          } finally {
            setProcessing(false);
          }
        },
        modal: { ondismiss: () => setProcessing(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (r) => {
        const desc = r.error?.description || 'Please try again.';
        toast.error('Payment failed: ' + desc);
        paymentsAPI.markFailed({ razorpay_order_id: data.order_id, reason: desc }).catch(() => {});
        setProcessing(false);
      });
      rzp.open();
    } catch (e) {
      toast.error('Could not start the payment. Please try again.');
      setProcessing(false);
    }
  };

  const handlePay = () => (provider === 'stripe' ? handlePayStripe() : handlePayRazorpay());

  if (step === 'success') {
    return (
      <div style={{ minHeight: '100vh', background: '#F1EAE1', display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="payment-success">
        <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 16, padding: '3rem', textAlign: 'center', maxWidth: 480 }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Payment Successful!</h2>
          <p style={{ fontSize: '0.9375rem', color: '#718096', marginBottom: '0.5rem' }}>Your <strong>{plan.name}</strong> plan is now active.</p>
          <p style={{ fontSize: '0.8125rem', color: '#A0AEC0', marginBottom: '2rem' }}>
            {plan.billing === 'annual' ? `${money(plan.billedTotal, currency)} billed annually` : `${money(plan.price, currency)}/month billed monthly`}
          </p>
          <Button data-testid="go-to-dashboard-btn" onClick={() => navigate('/dashboard')} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600, fontSize: '1rem', padding: '0.75rem 2.5rem', width: '100%' }}>
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const gatewayCopy = provider === 'stripe'
    ? { title: 'Card · Apple Pay · Google Pay', sub: '256-bit encrypted · Powered by Stripe' }
    : { title: 'UPI · Cards · Net Banking · Wallets', sub: '256-bit encrypted · Powered by Razorpay' };

  return (
    <div style={{ minHeight: '100vh', background: '#F1EAE1' }} data-testid="payment-page">
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.875rem 2rem', background: '#fff', borderBottom: '1px solid #F0EBE3' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => navigate('/')}>
          <div style={{ height: 36, borderRadius: 8, overflow: 'hidden' }}>
            <img src="/chire-logo-full.png" alt="CHIRE" style={{ height: '100%', objectFit: 'contain' }} />
          </div>
        </div>
        <span style={{ fontSize: '0.8125rem', color: '#718096' }}>Secure Checkout</span>
      </div>

      <div style={{ maxWidth: 860, margin: '2.5rem auto', padding: '0 1.5rem', display: 'grid', gridTemplateColumns: '1fr 360px', gap: '2rem' }} className="pay-grid">
        {/* Left: Checkout */}
        <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '2rem' }}>
          <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Complete your purchase</h2>
          <p style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '1.25rem' }}>
            {provider === 'stripe'
              ? "You'll be redirected to Stripe's secure checkout to pay by card."
              : "You'll be redirected to Razorpay's secure checkout to pay with UPI, cards, net banking or wallets."}
          </p>

          {/* Provider / currency toggle */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#A0AEC0', fontWeight: 700, marginBottom: '0.4rem' }}>Pay in</div>
            <div style={{ display: 'inline-flex', background: '#F1EAE1', borderRadius: 999, padding: '0.25rem', border: '1px solid #ECE7DE' }}>
              <button data-testid="provider-toggle-usd" onClick={() => chooseProvider('stripe')} style={{
                padding: '0.4rem 1.1rem', borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 700, transition: 'background 0.2s, color 0.2s',
                background: provider === 'stripe' ? '#002A38' : 'transparent', color: provider === 'stripe' ? '#fff' : '#78716C',
              }}>USD · Stripe</button>
              <button data-testid="provider-toggle-inr" onClick={() => chooseProvider('razorpay')} style={{
                padding: '0.4rem 1.1rem', borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 700, transition: 'background 0.2s, color 0.2s',
                background: provider === 'razorpay' ? '#002A38' : 'transparent', color: provider === 'razorpay' ? '#fff' : '#78716C',
              }}>INR · Razorpay</button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', borderRadius: 10, background: '#F0F6FF', border: '1px solid #D6E4FF', marginBottom: '1.5rem' }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0076A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#0076A3' }}>{gatewayCopy.title}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{gatewayCopy.sub}</div>
            </div>
          </div>

          <Button data-testid="pay-now-btn" onClick={handlePay} disabled={processing} style={{
            width: '100%', padding: '0.875rem', borderRadius: 10, fontWeight: 700, fontSize: '1rem',
            background: processing ? '#94A3B8' : '#0076A3', color: '#fff', cursor: processing ? 'wait' : 'pointer',
          }}>
            {processing ? 'Processing…' : `Pay ${totalLabel}`}
          </Button>

          <p style={{ fontSize: '0.6875rem', color: '#A0AEC0', textAlign: 'center', marginTop: '0.75rem' }}>
            By completing this purchase you agree to our{' '}
            <a href="/legal/msa" style={{ color: '#0076A3' }}>MSA</a> and <a href="/legal/privacy" style={{ color: '#0076A3' }}>Privacy Policy</a>.
          </p>
        </Card>

        {/* Right: Order Summary */}
        <div>
          <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem', position: 'sticky', top: 80 }}>
            <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 700, color: '#0076A3', marginBottom: '1.25rem' }}>Order Summary</h3>

            <div style={{ padding: '1rem', borderRadius: 10, background: '#FAF7F2', border: '1px solid #F0EBE3', marginBottom: '1rem' }}>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.25rem' }} data-testid="summary-plan-name">{plan.name} Plan</div>
              <div style={{ fontSize: '0.8125rem', color: '#718096' }}>{plan.billing === 'annual' ? 'Annual billing' : 'Monthly billing'}</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem', fontSize: '0.8125rem', color: '#4A5568' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Subscription</span>
                <span>{money(plan.price, currency)}/{plan.period}</span>
              </div>
              {plan.billing === 'annual' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#059669' }}>
                  <span>Annual discount</span>
                  <span>2 months free</span>
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid #F0EBE3', paddingTop: '0.75rem', display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1rem', color: '#0076A3' }}>
              <span>Total</span>
              <span data-testid="summary-total">{totalLabel}</span>
            </div>

            {plan.billing === 'annual' && plan.savings && (
              <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: 8, background: '#ECFDF5', border: '1px solid #D1FAE5', fontSize: '0.75rem', color: '#059669', textAlign: 'center' }}>
                You save {money(plan.savings, currency)}/year with annual billing
              </div>
            )}
          </Card>

          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button onClick={() => navigate('/pricing')} style={{ background: 'none', border: 'none', color: '#0076A3', cursor: 'pointer', fontSize: '0.8125rem' }}>
              Change plan
            </button>
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 720px){ .pay-grid{ grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}
